// ─────────────────────────────────────────────────────────────────────────────
// Pivota `GenerationalDistributionReportDto` (censo activo por OU × banda generacional × tipo de
// colaborador) para la hoja "Distribución Generacional" del export consolidado del panel Demográfico
// (`DemograficoExportButton.tsx`). Calcula aquí los derivados que el backend NO trae — % del total,
// rango de años, nota — porque son de PRESENTACIÓN, no de negocio (mismo criterio que `heatmapMatrix.ts`,
// que tampoco calcula nada de negocio, solo deriva ejes y lookups).
// ─────────────────────────────────────────────────────────────────────────────
import type { GenerationalDistributionEntryDto } from '@/api/recognition';
import { compararBandas, etiquetaDeBanda } from './DistribucionGeneracional';

/** Personas por debajo de las cuales una banda (ya NO suprimida por anonimato) se marca "Muestra
 *  pequeña" en la columna Nota — mismo umbral que usa la plantilla del cliente para Gen Alpha (7
 *  personas de 631, marcada así en `04 - Demografico.xlsx`). */
const UMBRAL_MUESTRA_PEQUENA = 10;

/** Rango de años visible por banda — mismo mapeo que `DistribucionGeneracional.tsx` (`BANDAS.anios`),
 *  duplicado aquí en vez de importado porque esa constante no está exportada (es un detalle interno
 *  de la tarjeta); si algún día diverge, la fuente de verdad es `GenerationHelper` de UserAdminService. */
const ANIOS_POR_BANDA: Record<string, string> = {
  GenAlpha: '2013–',
  GenZ: '1997–2012',
  Millennial: '1981–1996',
  GenX: '1965–1980',
  BabyBoomer: '1946–1964',
  Silent: '–1945',
};

/**
 * Valor de la columna «Nota» del Resumen Ejecutivo, como identificador canónico y no como texto: la
 * hoja se descarga en el idioma de quien exporta, y un literal aquí obligaría a esta función pura a
 * conocer i18n. Lo traduce `DemograficoExportButton` con `reports.generational.noteMajority` /
 * `noteSmallSample`.
 */
export type NotaDeBanda = 'mayoria' | 'muestraPequena' | '';

export interface FilaDistribucionGeneracional {
  banda: string;
  etiqueta: string;
  anios: string;
  total: number;
  pctDelTotal: number;
  porOu: ReadonlyMap<string, number>;
  frontline: number;
  staff: number;
  nota: NotaDeBanda;
  suprimido: boolean;
}

export interface MatrizDistribucionGeneracional {
  ous: string[];
  filas: FilaDistribucionGeneracional[];
}

/**
 * `ous` viene del backend (`GenerationalDistributionReportDto.ous`), no se deriva de las bandas: así
 * una OU con censo en alguna banda pero ausente de otra sigue apareciendo como columna (con 0), en
 * vez de desaparecer de la tabla.
 */
function notaDeBanda(esMayoria: boolean, esMuestraPequena: boolean): NotaDeBanda {
  if (esMayoria) return 'mayoria';
  return esMuestraPequena ? 'muestraPequena' : '';
}

export function buildGenerationalDistributionMatrix(
  bandas: readonly GenerationalDistributionEntryDto[],
  ous: readonly string[],
): MatrizDistribucionGeneracional {
  const totalGeneral = bandas.reduce((suma, banda) => suma + banda.total, 0);
  const maximo = Math.max(0, ...bandas.filter((banda) => !banda.suprimido).map((banda) => banda.total));

  // 🔴 Orden INVERSO al de la pantalla (`compararBandas` va de la más joven a la más veterana): la
  // plantilla del cliente lista Boomer → Gen X → Millennial → Gen Z → Gen Alpha, de la más veterana
  // a la más joven, y esta matriz sólo alimenta el Excel. La pantalla no se toca.
  const filas = [...bandas]
    .sort((bandaA, bandaB) => compararBandas(bandaB.generation, bandaA.generation))
    .map((banda): FilaDistribucionGeneracional => {
      const esMayoria = !banda.suprimido && maximo > 0 && banda.total === maximo;
      const esMuestraPequena = !banda.suprimido && banda.total > 0 && banda.total < UMBRAL_MUESTRA_PEQUENA;
      return {
        banda: banda.generation,
        etiqueta: etiquetaDeBanda(banda.generation),
        anios: ANIOS_POR_BANDA[banda.generation] ?? '',
        total: banda.total,
        pctDelTotal: totalGeneral > 0 ? banda.total / totalGeneral : 0,
        porOu: new Map(Object.entries(banda.porOu)),
        frontline: banda.frontline,
        staff: banda.staff,
        nota: notaDeBanda(esMayoria, esMuestraPequena),
        suprimido: banda.suprimido,
      };
    });

  return { ous: [...ous], filas };
}
